import { useState, useRef, useEffect } from 'react';
import { useSongs } from '@/context/SongContext';
import { db, generateId } from '@/lib/db';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mic, Square, Check, Loader2, Circle } from 'lucide-react';
import { formatDuration } from '@/lib/utils';
import { toast } from 'sonner';

export default function AudioRecorder({ songId, onComplete }) {
    const { addFragment } = useSongs();
    const [recording, setRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [duration, setDuration] = useState(0);
    const [label, setLabel] = useState('');
    const [saving, setSaving] = useState(false);
    const [permissionDenied, setPermissionDenied] = useState(false);
    const [audioLevels, setAudioLevels] = useState([]);

    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const startTimeRef = useRef(null);
    const analyserRef = useRef(null);
    const animationFrameRef = useRef(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
            if (audioUrl) URL.revokeObjectURL(audioUrl);
            if (mediaRecorderRef.current?.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
        };
    }, [audioUrl]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            // Set up audio analyser for visualization
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 32;
            source.connect(analyser);
            analyserRef.current = analyser;

            // Start visualization
            const updateLevels = () => {
                if (!analyserRef.current) return;
                const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
                analyserRef.current.getByteFrequencyData(dataArray);
                const levels = Array.from(dataArray.slice(0, 8)).map(v => v / 255);
                setAudioLevels(levels);
                animationFrameRef.current = requestAnimationFrame(updateLevels);
            };
            updateLevels();

            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: MediaRecorder.isTypeSupported('audio/webm')
                    ? 'audio/webm'
                    : 'audio/mp4'
            });
            
            mediaRecorderRef.current = mediaRecorder;
            chunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType });
                setAudioBlob(blob);
                setAudioUrl(URL.createObjectURL(blob));
                stream.getTracks().forEach(track => track.stop());
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                }
                setAudioLevels([]);
            };

            mediaRecorder.start(100);
            setRecording(true);
            setPermissionDenied(false);
            startTimeRef.current = Date.now();
            
            timerRef.current = setInterval(() => {
                setDuration((Date.now() - startTimeRef.current) / 1000);
            }, 100);
            
        } catch (error) {
            console.error('Error accessing microphone:', error);
            setPermissionDenied(true);
            toast.error('Microphone access denied. Please allow microphone access to record audio.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        setRecording(false);
    };

    const handleSave = async () => {
        if (!audioBlob) return;
        
        if (!songId) {
            console.error('Cannot save: songId is missing');
            toast.error('Cannot save recording - song not found');
            return;
        }

        setSaving(true);
        let blobId = null;
        
        try {
            blobId = generateId();
            
            // Step 1: Save audio blob to IndexedDB
            await db.saveAudioBlob(blobId, audioBlob);

            // Step 2: Create fragment and add to song
            const fragment = await addFragment(songId, {
                type: 'audio',
                label: label || 'Recording',
                blobId,
                duration,
                mimeType: audioBlob.type || 'audio/webm'
            });
            
            if (!fragment) {
                throw new Error('Fragment creation failed');
            }

            toast.success('Recording saved');
            onComplete(fragment.id);
        } catch (error) {
            console.error('Error saving audio:', error);
            
            // Clean up blob if it was saved but fragment creation failed
            if (blobId) {
                try { 
                    await db.deleteAudioBlob(blobId); 
                } catch (cleanupError) {
                    console.error('Failed to cleanup blob:', cleanupError);
                }
            }
            
            // Show user-friendly error message
            if (error.message === 'Song not found') {
                toast.error('Cannot save - song not found. Please refresh the page.');
            } else if (error.message === 'Song ID is required') {
                toast.error('Cannot save - invalid song. Please refresh the page.');
            } else {
                toast.error('Failed to save recording. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioBlob(null);
        setAudioUrl(null);
        setDuration(0);
        setLabel('');
    };

    return (
        <div className="space-y-6 max-w-sm mx-auto">
            {/* Recording UI */}
            {!audioBlob ? (
                <div className="flex flex-col items-center gap-6">
                    {/* Record button */}
                    <div className="relative">
                        {/* Outer glow ring when recording */}
                        {recording && (
                            <div className="absolute inset-0 rounded-full bg-destructive/20 animate-ping" />
                        )}
                        
                        <button
                            onClick={recording ? stopRecording : startRecording}
                            disabled={permissionDenied}
                            className={`relative w-28 h-28 rounded-full flex items-center justify-center transition-all duration-normal ${
                                recording
                                    ? 'bg-destructive hover:bg-destructive/90 shadow-lg'
                                    : 'bg-primary hover:bg-primary/90 shadow-md hover:shadow-lg'
                            } ${permissionDenied ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 active:scale-95'}`}
                        >
                            {recording ? (
                                <Square className="w-10 h-10 text-destructive-foreground" />
                            ) : (
                                <Mic className="w-12 h-12 text-primary-foreground" />
                            )}
                        </button>
                        
                        {/* Recording indicator dot */}
                        {recording && (
                            <div className="absolute top-1 right-1 flex items-center gap-1">
                                <Circle className="w-3 h-3 fill-destructive text-destructive recording-blink" />
                            </div>
                        )}
                    </div>

                    {/* Timer */}
                    <div className="text-center">
                        <span className={`text-5xl font-display text-foreground tabular-nums transition-all ${
                            recording ? 'text-destructive' : ''
                        }`}>
                            {formatDuration(duration)}
                        </span>
                        <p className="text-sm text-muted-foreground mt-2">
                            {recording ? (
                                <span className="text-destructive">Recording...</span>
                            ) : permissionDenied ? (
                                'Microphone access required'
                            ) : (
                                'Tap to record'
                            )}
                        </p>
                    </div>

                    {/* Audio visualization */}
                    {recording && (
                        <div className="flex items-center justify-center gap-1 h-12">
                            {(audioLevels.length > 0 ? audioLevels : [...Array(8)].map(() => 0.2)).map((level, i) => (
                                <div
                                    key={i}
                                    className="w-1.5 bg-destructive rounded-full transition-all duration-75"
                                    style={{
                                        height: `${Math.max(8, level * 48)}px`,
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                // Review recorded audio
                <div className="space-y-4 animate-fade-in">
                    <div className="p-4 rounded-xl bg-muted/50">
                        <audio src={audioUrl} controls className="w-full" />
                        <p className="text-center text-sm text-muted-foreground mt-2">
                            Duration: {formatDuration(duration)}
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="label">Label (optional)</Label>
                        <Input
                            id="label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g., Verse melody, Chorus idea"
                            className="hover:border-primary/50 transition-colors"
                        />
                    </div>

                    <div className="flex gap-3">
                        <Button
                            variant="outline"
                            onClick={handleReset}
                            className="flex-1 hover:scale-[1.02] active:scale-[0.98] transition-transform"
                        >
                            Record Again
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            className="flex-1 hover:scale-[1.02] active:scale-[0.98] transition-transform"
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Check className="w-4 h-4 mr-2" />
                            )}
                            Save
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}